import { z } from "zod";

/**
 * Standard API Success Response
 *
 * All successful API responses use this wrapper format for consistency.
 *
 * @example
 * {
 *   success: true,
 *   data: { id: "123", name: "Document" },
 *   meta: { total: 50, page: 1 }
 * }
 */
export const ApiSuccessSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    data: dataSchema,
    meta: z
      .object({
        total: z.number().optional(),
        page: z.number().optional(),
        limit: z.number().int().optional(),
        totalPages: z.number().int().optional(),
        hasMore: z.boolean().optional()
      })
      .optional()
  });

/**
 * Standard API Error Response
 *
 * All error responses use this format for consistent error handling.
 *
 * @example
 * {
 *   success: false,
 *   error: {
 *     message: "Deployment not found",
 *     code: "DEPLOYMENT_NOT_FOUND",
 *     details: { deploymentId: "123" }
 *   }
 * }
 */
export const ApiErrorSchema = z.object({
  success: z.literal(false),
  error: z.object({
    message: z.string(),
    code: z.string(),
    details: z.unknown().optional()
  })
});

/**
 * Type inference helper for success responses
 */
export type ApiSuccess<T> = {
  success: true;
  data: T;
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
    hasMore?: boolean;
  };
};

/**
 * Type inference helper for error responses
 */
export type ApiError = z.infer<typeof ApiErrorSchema>;

/**
 * Union type for all API responses
 *
 * Use this when a function can return either success or error.
 *
 * @example
 * async function fetchDocument(id: string): Promise<StandardApiResponse<Document>> {
 *   // ... returns ApiSuccess or ApiError
 * }
 */
export type StandardApiResponse<T> = ApiSuccess<T> | ApiError;

/**
 * Error code conventions:
 *
 * Format: {DOMAIN}_{REASON}
 *
 * Examples:
 * - DEPLOYMENT_NOT_FOUND
 * - DEPLOYMENT_ALREADY_EXISTS
 * - OAUTH_CONNECTION_EXPIRED
 * - VALIDATION_FAILED
 * - UNAUTHORIZED
 * - RATE_LIMIT_EXCEEDED
 */
export type ErrorCode = string;

// ============================================================================
// Runtime Helpers (Backend)
// ============================================================================

/**
 * Create success response
 *
 * Use in backend controllers to create standardized success responses.
 *
 * @example
 * return createApiSuccess(document);
 * // → { success: true, data: document }
 *
 * @example
 * return createApiSuccess({ documents }, { total: 50, hasMore: true });
 * // → { success: true, data: { documents }, meta: { total: 50, hasMore: true } }
 */
export function createApiSuccess<T>(
  data: T,
  meta?: { total?: number; page?: number; limit?: number; totalPages?: number; hasMore?: boolean }
): ApiSuccess<T> {
  return {
    success: true,
    data,
    ...(meta && { meta })
  };
}

/**
 * Create error response
 *
 * Use in backend controllers to create standardized error responses.
 *
 * @example
 * return createApiError("DEPLOYMENT_NOT_FOUND", "Deployment not found");
 * // → { success: false, error: { code: "DEPLOYMENT_NOT_FOUND", message: "..." } }
 *
 * @example
 * return createApiError("VALIDATION_FAILED", "Invalid email", { field: "email" });
 * // → { success: false, error: { ..., details: { field: "email" } } }
 */
export function createApiError(code: ErrorCode, message: string, details?: unknown): ApiError {
  const error: ApiError = {
    success: false,
    error: {
      code,
      message
    }
  };

  if (details !== undefined) {
    error.error.details = details;
  }

  return error;
}

/**
 * Check if response is success
 *
 * Type guard for discriminating success vs error responses.
 *
 * @example
 * if (isApiSuccess(response)) {
 *   console.log(response.data);  // ✅ TypeScript knows it's success
 * } else {
 *   console.error(response.error);  // ✅ TypeScript knows it's error
 * }
 */
export function isApiSuccess<T>(response: StandardApiResponse<T>): response is ApiSuccess<T> {
  return response.success === true;
}

/**
 * Check if response is error
 *
 * Type guard for discriminating error vs success responses.
 */
export function isApiError<T>(response: StandardApiResponse<T>): response is ApiError {
  return response.success === false;
}
